import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import {
  LocaleImportsService,
  ScriptLoaderService,
  SharedEventsService
} from '@fh-client-interaction-frontends/fh-providers';
import { NavigationEnd, Router } from '@angular/router';
import { Subscription, filter } from 'rxjs';
import { DefaultSettingsModel } from '@fh-client-interaction-frontends/fh-settings-generic';
import { TranslateService } from '@ngx-translate/core';
import { StylesVariablesEnum } from '@fh-client-interaction-frontends/fh-enums';
import { SharedSettingsService } from '@fh-client-interaction-frontends/fh-configurations';
import { Title } from '@angular/platform-browser';
import { FhApplicationSetupModel, FhApplicationSetupService } from '@fh-client-interaction-frontends/fh-setup-service';
import { APP_CONFIG, EnvironmentModel } from '@fh-client-interaction-frontends/fh-app-config';
import { GoogleTagManagerService } from 'angular-google-tag-manager';

@Component({
  selector: 'fh-client-interaction-frontends-root-cp',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss']
})
export class AppComponent implements OnInit, OnDestroy {
  appConfig: EnvironmentModel = new EnvironmentModel();
  loading = false;
  loadingSubscription = new Subscription();
  textSize = 'medium';
  defaultSettings: DefaultSettingsModel = new DefaultSettingsModel();
  nightMode: boolean = false;

  defaultColors = {
    [StylesVariablesEnum.DarkText]: '#262C3D',
    [StylesVariablesEnum.LightText]: '#576679',
    [StylesVariablesEnum.LightFill]: '#ffffff',
    [StylesVariablesEnum.LightBorder]: '#E3E3E3',
    [StylesVariablesEnum.MediumFill]: '#d9d9d9',
    [StylesVariablesEnum.InputBorderColor]: '#E3E3E3',
    [StylesVariablesEnum.InputFocusBorderColor]: '#262C3D',
    [StylesVariablesEnum.CardBorderColor]: '#E3E3E3',
    [StylesVariablesEnum.ModalBackdropBg]: '#262C3D',
    [StylesVariablesEnum.BackgroundPage]: '#E5E5E5',
    [StylesVariablesEnum.BackgroundControlsDark]: '#D9D9D9',
    [StylesVariablesEnum.BackgroundControlsDarker]: '#c3c3c3',
    [StylesVariablesEnum.BackgroundControlsMedium]: '#E6E6E6',
    [StylesVariablesEnum.BackgroundControlsLight]: '#DEDEDE',
    [StylesVariablesEnum.BackgroundControlsLighter]: '#F8F8F8',
    [StylesVariablesEnum.BsHeadingColor]: '#232323',
    [StylesVariablesEnum.BsBodyColor]: '#232323',
    [StylesVariablesEnum.BoxShadow]: '0px -30px 80px 0px rgba(0, 0, 0, 0.25)',
    [StylesVariablesEnum.BtnBoxShadow]: '0px 2px 25px -4px rgba(0,0,0,0.15)',
    [StylesVariablesEnum.CardBody]: '#ffffff',
    [StylesVariablesEnum.BtnSecondary]: '#576679',
    [StylesVariablesEnum.TableRow]: '#ffffff',
    [StylesVariablesEnum.TableText]: '#4B5563',
    [StylesVariablesEnum.BackgroundOverlayPopup]: '#E3E3E3',
    [StylesVariablesEnum.BoxShadowOverlayPopup]: '0px 0px 15px 1px rgba(0, 0, 0, 0.25)',
    [StylesVariablesEnum.secondaryColorText]: '#262C3D',
    [StylesVariablesEnum.welcomeBackground]: 'rgba(20, 30, 50, 1)'
  };
  nightColors = {
    [StylesVariablesEnum.DarkText]: '#ffffff',
    [StylesVariablesEnum.LightText]: '#E5E5E5',
    [StylesVariablesEnum.LightFill]: '#2C2F3D',
    [StylesVariablesEnum.LightBorder]: '#2C2F3D',
    [StylesVariablesEnum.MediumFill]: '#3B3F4D',
    [StylesVariablesEnum.InputBorderColor]: '#2C2F3D',
    [StylesVariablesEnum.InputFocusBorderColor]: '#ffffff',
    [StylesVariablesEnum.CardBorderColor]: '#2C2F3D',
    [StylesVariablesEnum.ModalBackdropBg]: '#ffffff',
    [StylesVariablesEnum.BackgroundPage]: '#232323',
    [StylesVariablesEnum.BackgroundControlsDark]: '#262C3D',
    [StylesVariablesEnum.BackgroundControlsDarker]: '#2C2F3D',
    [StylesVariablesEnum.BackgroundControlsMedium]: '#232323',
    [StylesVariablesEnum.BackgroundControlsLight]: '#575F75',
    [StylesVariablesEnum.BackgroundControlsLighter]: '#6C768F',
    [StylesVariablesEnum.BsHeadingColor]: '#ffffff',
    [StylesVariablesEnum.BsBodyColor]: '#ffffff',
    [StylesVariablesEnum.BoxShadow]: '0px -30px 80px 0px rgba(128, 128, 128, 0.25)',
    [StylesVariablesEnum.BtnBoxShadow]: '0 1px 6px 4px #ffffff26',
    [StylesVariablesEnum.CardBody]: '#2C2F3D',
    [StylesVariablesEnum.BtnSecondary]: '#2C2F3D',
    [StylesVariablesEnum.TableRow]: '#2C2F3D',
    [StylesVariablesEnum.TableText]: '#ffffff',
    [StylesVariablesEnum.BackgroundOverlayPopup]: '#222222',
    [StylesVariablesEnum.BoxShadowOverlayPopup]: '0px 0px 7px -1px rgba(128, 128, 128, 0.15)',
    [StylesVariablesEnum.secondaryColorText]: '#ffffff',
    [StylesVariablesEnum.welcomeBackground]: '#353535'
  };

  constructor(
    private sharedEventsService: SharedEventsService,
    private router: Router,
    private sharedSettingsService: SharedSettingsService,
    private translateService: TranslateService,
    private scriptLoaderService: ScriptLoaderService,
    private localeImportsService: LocaleImportsService,
    private titleService: Title,
    private fhApplicationSetupService: FhApplicationSetupService,
    @Inject(APP_CONFIG) protected appConfigTemp: EnvironmentModel,
    private gtmService: GoogleTagManagerService
  ) {
    this.appConfig = appConfigTemp;
    this.scriptLoaderService
      .loadScript('googleMaps')
      .then(() => {})
      .catch((error) => {
        console.error('Google Maps script could not be loaded.', error);
      });

    if (this.appConfig.scripts?.googleAnalytics?.data?.trackingId) {
      this.gtmService.addGtmToDom();
      this.router.events.pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd)
      ).subscribe((event) => {
        this.gtmService.pushTag({ event: 'page_view', pagePath: event.urlAfterRedirects });
      });
    }

    this.scriptLoaderService.loadPixelMetaScript();
    this.scriptLoaderService.loadCookieConsent();
  }

  ngOnInit() {
    this.titleService.setTitle(this.translateService.instant(this.appConfig.platform + '.title'));
    this.backgroundsEvents();
  }
  ngOnDestroy() {
    this.loadingSubscription.unsubscribe();
  }

  backgroundsEvents() {
    if (!localStorage.getItem('settings')) {
      localStorage.setItem('settings', JSON.stringify(this.defaultSettings));
      this.sharedSettingsService.set(this.defaultSettings);
    }
    this.getSetup();
    this.getLocalSettings();

    this.router.events.subscribe({
      next: (event: any) => {
        if (event.type == 15) {
          this.sharedEventsService.removeLoading();
        }
      }
    });

    this.loadingSubscription = this.sharedEventsService.loadingEmitter.subscribe({
      next: (event: boolean) => {
        this.loading = event;
      }
    });
  }

  getSetup() {
    this.fhApplicationSetupService.getSetup().subscribe({
      next: (setup: FhApplicationSetupModel) => {
        this.localeImportsService.loadLocale(setup.locale).then((result) => {
          this.fhApplicationSetupService.emitSetup(setup);
        });
      }
    });
  }
  getLocalSettings() {
    this.sharedSettingsService.get().subscribe({
      next: (value: any) => {
        this.textSize = value?.textSize;
        this.nightMode = value?.nightMode;
        this.translateService.use(value?.defaultLanguage);
        if (this.textSize) {
          this.applyFontSizeClass();
        }
        if (this.nightMode) {
          this.setNightMode();
        } else {
          this.removeNightMode();
        }

        if(value.dyslexicFont) {
          this.setFont('opendyslexic');
        } else {
          this.setFont('inter');
        }
      }
    });
  }

  setFont(font: 'inter' | 'opendyslexic') {
    const body = document.body;
    body.classList.remove('font-inter', 'font-opendyslexic');
    body.classList.add(`font-${font}`);
    localStorage.setItem('preferredFont', font);
  }

  applyFontSizeClass() {
    document.body?.classList.remove('font-size-small', 'font-size-medium', 'font-size-large');
    document.body?.classList.add(`font-size-${this.textSize}`);
  }
  setNightMode() {
    for (const [variable, defaultValue] of Object.entries(this.nightColors)) {
      document.documentElement.style.setProperty(variable, defaultValue);
    }
  }
  removeNightMode() {

    for (const [variable, defaultValue] of Object.entries(this.defaultColors)) {
      document.documentElement.style.setProperty(variable, defaultValue);
    }
  }
}
